import { useState } from "react";
import { useTranslation } from "react-i18next";
import { InboxOutlined } from "@ant-design/icons";
import { Flex } from "antd";

import type { UploadFileExtended } from "@av/apps/public/jobs/services/apply/types";
import {
  ALLOWED_UPLOAD_DOCUMENT_EXTENSIONS,
  getDocumentUploadValidationError,
  MAX_SUPPORTING_DOCUMENTS_COUNT,
} from "@av/apps/recruitment/ats-common/services/document-upload-validation";
import { Alert } from "@av/components/atoms/Alert";
import { Modal } from "@av/components/atoms/Modal";
import { Typography } from "@av/components/atoms/typography/Typography";
import { Upload } from "@av/components/atoms/upload/Upload";
import { UploadDragger } from "@av/components/atoms/upload/UploadDragger";
import { UploadDropBoundary } from "./UploadDropBoundary";
import { UploadedDocumentModalFileRow } from "./UploadedDocumentModalFileRow";
import {
  type SupportingDocumentDraft,
  type SupportingDocumentValidationIssue,
  useSupportingDocumentDrafts,
} from "./useSupportingDocumentDrafts";

import type { UploadFile } from "antd/es/upload/interface";
import type { TFunction } from "i18next";

type SupportingDocumentsUploadModalProps = {
  fileList: UploadFileExtended[];
  initialReplacement?: {
    fileUid: string;
    replacementFile: UploadFile;
  };
  onCancel: () => void;
  onConfirm: (files: UploadFileExtended[]) => void;
};

const OVERFLOW_ERROR_ID = "supporting-documents-upload-overflow-error";
const SUPPORTING_DOCUMENTS_ERROR_TRANSLATION_KEYS = {
  FILE_TYPE: "recruitment.jobApplication.documents_supportingDocuments_fileTypeUploadError",
  FILE_SIZE: "recruitment.jobApplication.documents_supportingDocuments_fileSizeUploadError",
} as const;
const getValidationIssueId = (
  validationError: SupportingDocumentValidationIssue["validationError"]
) =>
  `supporting-documents-upload-${
    validationError === "FILE_TYPE" ? "file-type" : "file-size"
  }-error`;

function resolveSupportingDocumentUploadValidationErrorTranslation(
  t: TFunction,
  validationError: SupportingDocumentValidationIssue["validationError"],
  fileNames: string[]
) {
  const translationKey = SUPPORTING_DOCUMENTS_ERROR_TRANSLATION_KEYS[validationError];

  if (fileNames.length === 1) {
    return t(translationKey, {
      count: 1,
      fileName: fileNames[0],
    });
  }

  return t(translationKey, {
    count: fileNames.length,
    fileNames: fileNames.join(", "),
  });
}

function getSupportingDocumentValidationIssues(
  files: ArrayLike<Pick<File, "name" | "size" | "type">>
) {
  const issues = new Map<SupportingDocumentValidationIssue["validationError"], string[]>();

  Array.from(files).forEach((file) => {
    const validationError = getDocumentUploadValidationError(file);

    if (!validationError) {
      return;
    }

    const nextFileNames = issues.get(validationError) ?? [];

    nextFileNames.push(file.name);
    issues.set(validationError, nextFileNames);
  });

  return Array.from(issues.entries()).map(([validationError, fileNames]) => ({
    validationError,
    fileNames,
  }));
}

export function SupportingDocumentsUploadModal({
  fileList,
  initialReplacement,
  onCancel,
  onConfirm,
}: SupportingDocumentsUploadModalProps) {
  const { t } = useTranslation();
  const {
    committedFiles,
    draftFiles,
    replaceSelectedFiles,
    validationIssues: initialValidationIssues,
  } = useSupportingDocumentDrafts(fileList, initialReplacement);
  const [validationIssues, setValidationIssues] = useState(initialValidationIssues);
  const excessCount = Math.max(0, draftFiles.length - MAX_SUPPORTING_DOCUMENTS_COUNT);
  const isDropzoneDisabled = draftFiles.length >= MAX_SUPPORTING_DOCUMENTS_COUNT;
  const isAddDisabled = draftFiles.length === 0 || excessCount > 0;
  const describedBy = [
    ...validationIssues.map(({ validationError }) => getValidationIssueId(validationError)),
    ...(excessCount > 0 ? [OVERFLOW_ERROR_ID] : []),
  ].join(" ");

  return (
    <Modal
      testIdPrefix="supporting-documents-upload"
      title={t("recruitment.jobApplication.documents_addSupportingDocuments")}
      open
      maskClosable={false}
      onCancel={onCancel}
      onOk={() => onConfirm(committedFiles)}
      okText={t("recruitment.common.global_add")}
      cancelText={t("recruitment.common.global_cancel")}
      okButtonProps={{ disabled: isAddDisabled }}
      width="520px"
      modalRender={(modal) => <UploadDropBoundary>{modal}</UploadDropBoundary>}
    >
      <Flex vertical gap={16}>
        <Alert
          testIdPrefix="supporting-documents-upload-naming-hint"
          message={t("recruitment.jobApplication.documents_supportingDocumentsNamingHint")}
          type="info"
          showIcon={false}
          style={{ margin: 0 }}
        />
        <UploadDragger
          testIdPrefix="supporting-documents-upload-dropzone"
          accessibility={{
            invalid: validationIssues.length > 0 || excessCount > 0,
            describedBy: describedBy || undefined,
            label: t("recruitment.jobApplication.documents_clickDragFiles", {
              count: MAX_SUPPORTING_DOCUMENTS_COUNT,
            }),
          }}
          multiple
          disabled={isDropzoneDisabled}
          fileList={draftFiles}
          itemRender={(_, file, __, actions) => {
            const draftFile = file as SupportingDocumentDraft;

            return <UploadedDocumentModalFileRow file={draftFile} onRemove={actions.remove} />;
          }}
          accept={ALLOWED_UPLOAD_DOCUMENT_EXTENSIONS.join(",")}
          beforeUpload={(file) => {
            if (getDocumentUploadValidationError(file)) {
              return Upload.LIST_IGNORE;
            }

            return false;
          }}
          onChangeCapture={(event) => {
            const input = event.target as HTMLInputElement | null;

            if (!input?.files) {
              return;
            }

            setValidationIssues(getSupportingDocumentValidationIssues(input.files));
          }}
          onDropCapture={(event) => {
            setValidationIssues(getSupportingDocumentValidationIssues(event.dataTransfer.files));
          }}
          onChange={({ fileList: files }) => {
            replaceSelectedFiles(files);
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {t("recruitment.jobApplication.documents_clickDragFiles", {
              count: MAX_SUPPORTING_DOCUMENTS_COUNT,
            })}
          </p>
          <p className="ant-upload-hint" style={{ marginBottom: 0 }}>
            {t("recruitment.jobApplication.documents_supportingDocumentsUploadHint")}
          </p>
        </UploadDragger>
        <Flex vertical gap={12} aria-live="polite">
          {validationIssues.map(({ validationError, fileNames }) => (
            <Typography.Text
              key={`${validationError}-${fileNames.join(",")}`}
              id={getValidationIssueId(validationError)}
              testIdPrefix={getValidationIssueId(validationError)}
              role="alert"
              type="danger"
              style={{ whiteSpace: "pre-line" }}
            >
              {resolveSupportingDocumentUploadValidationErrorTranslation(
                t,
                validationError,
                fileNames
              )}
            </Typography.Text>
          ))}
          {excessCount > 0 ? (
            <Typography.Text
              id={OVERFLOW_ERROR_ID}
              testIdPrefix="supporting-documents-upload-overflow"
              role="alert"
              type="danger"
            >
              {t("recruitment.jobApplication.documents_supportingDocumentsOverflow", {
                count: excessCount,
              })}
            </Typography.Text>
          ) : null}
        </Flex>
      </Flex>
    </Modal>
  );
}
